import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './Digital.scss';

// ── Canvas neural network ─────────────────────────────────
const NeuralCanvas = () => {
    const canvasRef = useRef(null);
    useEffect(() => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        let animId;
        let W, H;
        const NODES = 80;
        const MAX_DIST = 160;
        const nodes = [];

        const resize = () => { W = canvas.width = window.innerWidth; H = canvas.height = window.innerHeight; };
        const init = () => {
            nodes.length = 0;
            for (let i = 0; i < NODES; i++) nodes.push({
                x: Math.random() * W, y: Math.random() * H,
                vx: (Math.random() - 0.5) * 0.4, vy: (Math.random() - 0.5) * 0.4,
                r: Math.random() * 1.5 + 0.5,
            });
        };

        const draw = () => {
            ctx.clearRect(0, 0, W, H);
            for (let i = 0; i < nodes.length; i++) {
                for (let j = i + 1; j < nodes.length; j++) {
                    const dx = nodes[i].x - nodes[j].x;
                    const dy = nodes[i].y - nodes[j].y;
                    const dist = Math.sqrt(dx * dx + dy * dy);
                    if (dist < MAX_DIST) {
                        ctx.beginPath();
                        ctx.strokeStyle = `rgba(120,160,255,${(1 - dist / MAX_DIST) * 0.35})`;
                        ctx.lineWidth = 0.6;
                        ctx.moveTo(nodes[i].x, nodes[i].y);
                        ctx.lineTo(nodes[j].x, nodes[j].y);
                        ctx.stroke();
                    }
                }
            }
            for (const n of nodes) {
                ctx.beginPath();
                ctx.arc(n.x, n.y, n.r, 0, Math.PI * 2);
                ctx.fillStyle = 'rgba(140,180,255,0.7)';
                ctx.fill();
                n.x += n.vx; n.y += n.vy;
                if (n.x < 0 || n.x > W) n.vx *= -1;
                if (n.y < 0 || n.y > H) n.vy *= -1;
            }
            animId = requestAnimationFrame(draw);
        };

        resize(); init(); draw();
        window.addEventListener('resize', () => { resize(); init(); });
        const onMove = (e) => {
            for (const n of nodes) {
                const dx = n.x - e.clientX, dy = n.y - e.clientY;
                const dist = Math.sqrt(dx * dx + dy * dy);
                if (dist < 120) {
                    n.vx += (dx / dist) * 0.3; n.vy += (dy / dist) * 0.3;
                    const speed = Math.sqrt(n.vx * n.vx + n.vy * n.vy);
                    if (speed > 2) { n.vx = (n.vx / speed) * 2; n.vy = (n.vy / speed) * 2; }
                }
            }
        };
        window.addEventListener('mousemove', onMove);
        return () => { cancelAnimationFrame(animId); window.removeEventListener('mousemove', onMove); };
    }, []);
    return <canvas ref={canvasRef} className="dg-canvas" />;
};

// ── Animated counter ──────────────────────────────────────
const Counter = ({ target, suffix = '' }) => {
    const ref = useRef(null);
    useEffect(() => {
        const el = ref.current;
        const observer = new IntersectionObserver(([entry]) => {
            if (!entry.isIntersecting) return;
            observer.disconnect();
            let start = 0;
            const step = () => {
                start += target / 60;
                if (start >= target) { el.textContent = Math.ceil(target) + suffix; return; }
                el.textContent = Math.floor(start) + suffix;
                requestAnimationFrame(step);
            };
            requestAnimationFrame(step);
        }, { threshold: 0.5 });
        observer.observe(el);
        return () => observer.disconnect();
    }, [target, suffix]);
    return <span ref={ref}>0{suffix}</span>;
};

// ── Main ──────────────────────────────────────────────────
const Digital = () => {
    const { t } = useTranslation();
    const [heroReady, setHeroReady] = useState(false);
    const sectionsRef = useRef([]);

    const services = t('digital.services', { returnObjects: true });
    const stats = t('digital.stats', { returnObjects: true });
    const steps = t('digital.steps', { returnObjects: true });
    const marquee = t('digital.marquee', { returnObjects: true });

    const colors = ['#4f8ef7', '#a855f7', '#C9A84C'];

    useEffect(() => {
        window.scrollTo(0, 0);
        const timer = setTimeout(() => setHeroReady(true), 200);
        const obs = new IntersectionObserver(
            entries => entries.forEach(e => { if (e.isIntersecting) e.target.classList.add('dg-visible'); }),
            { threshold: 0.08 }
        );
        sectionsRef.current.forEach(el => el && obs.observe(el));
        return () => { clearTimeout(timer); obs.disconnect(); };
    }, []);

    const addRef = i => el => sectionsRef.current[i] = el;

    return (
        <div className="dg-page">

            {/* HERO */}
            <section className="dg-hero">
                <NeuralCanvas />
                <div className="dg-hero-vignette" />
                <div className="dg-hero-content">
                    <div className={`dg-badge ${heroReady ? 'dg-badge--in' : ''}`}>
                        <span className="dg-badge-dot" />
                        {t('digital.badge')}
                    </div>

                    <div className="dg-hero-title">
                        <div className="dg-reveal-line">
                            <span className="dg-reveal-inner" style={{
                                transitionDelay: '0.3s',
                                transform: heroReady ? 'translateY(0)' : 'translateY(110%)',
                                opacity: heroReady ? 1 : 0,
                            }}>
                                {t('digital.hero_line1')}
                            </span>
                        </div>
                        <div className="dg-reveal-line">
                            <span
                                className="dg-reveal-inner dg-glitch"
                                data-text={t('digital.hero_line2')}
                                style={{
                                    transitionDelay: '0.46s',
                                    transform: heroReady ? 'translateY(0)' : 'translateY(110%)',
                                    opacity: heroReady ? 1 : 0,
                                }}
                            >
                                {t('digital.hero_line2')}
                            </span>
                        </div>
                    </div>

                    <div className="dg-hero-sub">
                        {t('digital.hero_sub').split(' ').map((word, i) => (
                            <span key={i} className="dg-word-wrap">
                                <span className="dg-word" style={{
                                    transitionDelay: `${0.65 + i * 0.035}s`,
                                    transform: heroReady ? 'translateY(0)' : 'translateY(120%)',
                                    opacity: heroReady ? 1 : 0,
                                }}>
                                    {word}
                                </span>
                            </span>
                        ))}
                    </div>

                    <div className={`dg-hero-actions ${heroReady ? 'dg-actions--in' : ''}`}>
                        <Link to="/contact" className="dg-btn-primary">
                            {t('digital.cta_start')} <span>→</span>
                        </Link>
                        <a href="#services" className="dg-btn-ghost">{t('digital.cta_see')}</a>
                    </div>
                </div>

                <div className="dg-scroll-indicator">
                    <div className="dg-scroll-mouse"><div className="dg-scroll-wheel" /></div>
                </div>
            </section>

            {/* MARQUEE */}
            <div className="dg-marquee-wrap">
                <div className="dg-marquee">
                    {[...Array(2)].flatMap((_, ri) =>
                        Array.isArray(marquee) && marquee.map((item, i) => (
                            <span key={`${ri}-${i}`} className="dg-marquee-item">
                                {item} <em>·</em>
                            </span>
                        ))
                    )}
                </div>
            </div>

            {/* SERVICES */}
            <section className="dg-services dg-reveal" id="services" ref={addRef(0)}>
                <div className="dg-inner">
                    <p className="dg-label">{t('digital.services_label')}</p>
                    <h2 className="dg-title">{t('digital.services_title')}</h2>
                    <div className="dg-cards">
                        {Array.isArray(services) && services.map((s, i) => (
                            <Link to={s.slug} key={s.num} className="dg-card"
                                style={{ '--card-color': colors[i], transitionDelay: `${i * 0.1}s` }}>
                                <div className="dg-card-beam" />
                                <div className="dg-card-top">
                                    <span className="dg-card-num">{s.num}</span>
                                    <span className="dg-card-icon">{s.icon}</span>
                                </div>
                                <h3>{s.title}</h3>
                                <p>{s.desc}</p>
                                <div className="dg-tags">
                                    {s.tags.map(tag => <span key={tag}>{tag}</span>)}
                                </div>
                                <div className="dg-card-cta">En savoir plus <span>↗</span></div>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            {/* STATS */}

            <section className="dg-stats" ref={addRef(2)}>
                <div className="dg-inner dg-stats-grid">
                    {Array.isArray(stats) && stats.map((s, i) => (
                        <div className="dg-stat" key={i}>
                            <div className="dg-stat-num">
                                <Counter target={s.val} suffix={s.suffix} />
                            </div>
                            <p>{s.label}</p>
                        </div>
                    ))}
                </div>
            </section>
            {/* ── FAZA Partnership ────────────────────────────────────── */}
            <section className="dg-faza dg-reveal" ref={addRef(1)}>
                <div className="dg-inner">
                    <div className="dg-faza-inner">
                        <div className="dg-faza-badge">
                            <span className="dg-faza-dot" />
                            {t('digital.faza.eyebrow')}
                        </div>
                        <div className="dg-faza-body">
                            <div className="dg-faza-text">
                                <h2>{t('digital.faza.title')}</h2>
                                <p>{t('digital.faza.desc')}</p>
                                <Link to="/partenariat-faza" className="dg-faza-link">
                                    {t('digital.faza.cta')} <span>→</span>
                                </Link>
                            </div>
                            <div className="dg-faza-pills">
                                {Array.isArray(t('digital.faza.tags', { returnObjects: true })) &&
                                    t('digital.faza.tags', { returnObjects: true }).map(tag => (
                                        <span key={tag} className="dg-faza-pill">{tag}</span>
                                    ))
                                }
                            </div>
                        </div>
                    </div>
                </div>
            </section>



            {/* PROCESS */}
            <section className="dg-process dg-reveal" ref={addRef(2)}>
                <div className="dg-inner">
                    <p className="dg-label">{t('digital.process_label')}</p>
                    <h2 className="dg-title">{t('digital.process_title')}</h2>
                    <div className="dg-steps">
                        {Array.isArray(steps) && steps.map((step, i) => (
                            <div className="dg-step" key={step.num}
                                style={{ transitionDelay: `${i * 0.12}s` }}>
                                <span className="dg-step-num">{step.num}</span>
                                <h3>{step.title}</h3>
                                <p>{step.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="dg-cta dg-reveal" ref={addRef(3)}>
                <NeuralCanvas />
                <div className="dg-cta-vignette" />
                <div className="dg-cta-inner">
                    <p className="dg-label">{t('digital.cta_label')}</p>
                    <h2>
                        <span className="dg-reveal-line">
                            <span className="dg-reveal-inner">{t('digital.cta_title1')}</span>
                        </span>
                        <span className="dg-reveal-line">
                            <span className="dg-reveal-inner dg-glitch"
                                data-text={t('digital.cta_title2')}>
                                {t('digital.cta_title2')}
                            </span>
                        </span>
                    </h2>
                    <p className="dg-cta-sub">{t('digital.cta_sub')}</p>
                    <Link to="/contact" className="dg-btn-primary large">
                        {t('digital.cta_btn')} <span>→</span>
                    </Link>
                </div>
            </section>

        </div>
    );
};

export default Digital;
